import React, { FC, useState } from 'react';
import cs from 'classnames';
import s from './Template.scss';
import { Button, Text } from '@wix/design-system';
import {
  isBlankTemplate,
  Template as TemplateData,
} from '../../../../domain/entities/template';
import { useTranslation } from '@wix/wix-i18n-config';
import { useIsMounted } from '../../../../hooks/useIsMounted';
import useTimeout from '../../../../hooks/useTimeout';
import { TemplateImage } from './TemplateImage/TemplateImage';
import { useBiLogger } from '../../../../contexts/BILoggerContext';
import { templateDataHooks } from './Template.dataHooks';
import { useEditUrl } from '../../../../hooks/useEditUrl';
import { useViewUrl } from '../../../../hooks/useViewUrl';
import { useOnViewportEnter } from '../../../../hooks/useOnViewportEnter';

export interface TemplateProps {
  template: TemplateData;
  isLazyLoad?: boolean;
  index: number;
}

export const Template: FC<TemplateProps> = ({
  template,
  isLazyLoad,
  index,
}) => {
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isImageDelayLoading, setIsImageDelayLoading] = useState(false);
  const editUrl = useEditUrl(template);
  const viewUrl = useViewUrl(template);
  const isMounted = useIsMounted();
  const desktop16X9Image = template.assets?.find(
    (asset) => asset.type === 'DESKTOP_16X9',
  );
  const desktopImage = template.assets?.find(
    (asset) => asset.type === 'DESKTOP',
  );
  const image = desktop16X9Image || desktopImage;
  const timeoutDelay = 300;

  const { t } = useTranslation();
  const timeout = useTimeout();
  const biLogger = useBiLogger();
  const onEditClick = () => {
    biLogger.reportEditTemplate({ ...template, index });
  };

  const onViewClick = () => {
    biLogger.reportViewTemplate({ ...template, index });
  };

  const onImageLoadStart = () => {
    setIsImageLoading(true);
    setIsImageDelayLoading(true);
    timeout.set(() => {
      timeout.clear();
      setIsImageDelayLoading(false);
    }, timeoutDelay);
  };

  const templateRef = useOnViewportEnter<HTMLDivElement>(() => {
    biLogger.reportAboveFoldImpression({ ...template, index });
  });

  return (
    <div
      className={s.template}
      data-hook={templateDataHooks.template({ key: template.templateId })}
      ref={templateRef}
    >
      {image && (
        <div className={s.templateImageContainer}>
          <div
            className={cs(s.templateImageLoadingContainer, {
              [s.animationOverImage]: isMounted,
              [s.loading]: isImageLoading || isImageDelayLoading,
            })}
          ></div>
          <TemplateImage
            className={s.templateImage}
            desktop16x9Img={desktop16X9Image?.url}
            isLazyLoad={isLazyLoad}
            previewImg={desktopImage?.url}
            alt={template.title}
            onLoadStart={() => onImageLoadStart()}
            onLoad={() => setIsImageLoading(false)}
          />
          <a
            target="_blank"
            className={cs(s.viewLink, s.hideOnDesktop)}
            href={viewUrl}
            rel="noreferrer"
          >
            {t('app.templates.grid.view.button')}
          </a>
          <div className={cs(s.actionButtonsContainer, s.hideOnMobile)}>
            <Button
              as="a"
              size="large"
              className={s.editButton}
              skin="light"
              target="_blank"
              onClick={onEditClick}
              href={editUrl}
              dataHook={templateDataHooks.templateEditButton({
                key: template.templateId,
              })}
            >
              {t('app.templates.grid.edit.button')}
            </Button>
            {!isBlankTemplate(template) && (
              <Button
                as="a"
                priority="secondary"
                size="large"
                target="_blank"
                skin="light"
                className={s.viewButton}
                onClick={onViewClick}
                href={viewUrl}
                dataHook={templateDataHooks.templateViewButton({
                  key: template.templateId,
                })}
              >
                {t('app.templates.grid.view.button')}
              </Button>
            )}
          </div>
        </div>
      )}
      <div className={s.templateName}>
        <Text size="medium" weight="thin" textAlign="start">
          {template.title}
        </Text>
      </div>
    </div>
  );
};
